import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Plus, Search, Briefcase, ChevronDown, X, Archive, FolderOpen, CheckCircle2 } from 'lucide-react';
import SwipeableRow from '../components/SwipeableRow';
import {
  Card, Button, Input, Select, Badge, EmptyState, Modal, Label, Textarea, StatusDropdown,
  SkeletonListRows, HelpTooltip,
} from '../components/ui';
import { useFirm } from '../contexts/FirmContext';
import { useAuth } from '../contexts/AuthContext';
import {
  listMatters, createMatter, updateMatter, archiveMatter, closeMatter, reopenMatter, listContacts,
  listFirmUsers, addMatterUser,
  listEmployees, addMatterTeamMember,
} from '../lib/db';
import { PRACTICE_AREAS, MATTER_STATUS, MATTER_PRIORITY, FEE_ARRANGEMENTS, GHANA_JURISDICTIONS } from '../lib/constants';
import { fullName, formatDate, classNames } from '../lib/formatters';
import { useRoute } from '../lib/router';
import { useActivityLogger } from '../lib/useActivityLogger';
import toast from '../lib/toast';
import FirstVisitTips from '../components/onboarding/FirstVisitTips';
import { withTimeout } from '../lib/fetchWithTimeout';

// ─── Multi-select dropdown ────────────────────────────────────────────────────
// options: [{ value, label, sublabel?, badge? }]
// selected: string[]   (array of values)
// onChange: (value) => void  (toggles one item at a time)
function MultiSelectDropdown({ options = [], selected = [], onChange, placeholder = 'Select…' }) {
  const [open, setOpen]     = useState(false);
  const [search, setSearch] = useState('');
  const ref = useRef(null);

  // Close on outside click
  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const filtered = useMemo(() =>
    options.filter((o) => !search || o.label.toLowerCase().includes(search.toLowerCase())),
  [options, search]);

  const selectedOptions = options.filter((o) => selected.includes(o.value));

  return (
    <div ref={ref} className="relative">
      {/* Trigger button */}
      <button
        type="button"
        onClick={() => { setOpen((v) => !v); setSearch(''); }}
        className="w-full flex items-start gap-2 px-3 py-2 min-h-[38px] rounded-lg border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-800 text-left text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 transition"
      >
        <div className="flex-1 flex flex-wrap gap-1">
          {selectedOptions.length === 0 ? (
            <span className="text-slate-400">{placeholder}</span>
          ) : (
            selectedOptions.map((o) => (
              <span
                key={o.value}
                className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-indigo-100 dark:bg-indigo-500/20 text-indigo-700 dark:text-indigo-300 text-xs font-medium"
              >
                {o.label}
                <button
                  type="button"
                  onClick={(e) => { e.stopPropagation(); onChange(o.value); }}
                  className="hover:text-indigo-900 dark:hover:text-white"
                >
                  <X size={10} />
                </button>
              </span>
            ))
          )}
        </div>
        <ChevronDown size={14} className={`flex-shrink-0 mt-0.5 text-slate-400 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>

      {/* Dropdown panel */}
      {open && (
        <div className="absolute z-50 mt-1 w-full bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg shadow-lg overflow-hidden">
          {/* Search */}
          <div className="p-2 border-b border-slate-100 dark:border-slate-700">
            <input
              autoFocus
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search…"
              className="w-full text-sm px-2 py-1.5 rounded-md bg-slate-50 dark:bg-slate-700 border-0 focus:outline-none focus:ring-1 focus:ring-indigo-500 text-slate-900 dark:text-white placeholder-slate-400"
            />
          </div>
          {/* Options */}
          <ul className="max-h-48 overflow-y-auto divide-y divide-slate-50 dark:divide-slate-700/50">
            {filtered.length === 0 ? (
              <li className="px-3 py-3 text-xs text-slate-400 text-center">No results</li>
            ) : (
              filtered.map((o) => {
                const checked = selected.includes(o.value);
                return (
                  <li key={o.value}>
                    <label className="flex items-center gap-3 px-3 py-2.5 cursor-pointer hover:bg-slate-50 dark:hover:bg-slate-700/50 transition">
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={() => onChange(o.value)}
                        className="rounded border-slate-300 text-indigo-600 focus:ring-indigo-500"
                      />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-slate-900 dark:text-slate-100 truncate">{o.label}</p>
                        {o.sublabel && <p className="text-xs text-slate-500 truncate">{o.sublabel}</p>}
                      </div>
                      {o.badge && (
                        <span className="text-[10px] px-1.5 py-0.5 rounded bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300 flex-shrink-0">
                          {o.badge}
                        </span>
                      )}
                    </label>
                  </li>
                );
              })
            )}
          </ul>
        </div>
      )}
    </div>
  );
}

// ─── Composite key helpers ────────────────────────────────────────────────────
const userKey     = (id) => `u:${id}`;
const employeeKey = (id) => `e:${id}`;

// ─── Blank form ───────────────────────────────────────────────────────────────
const blankMatter = {
  matter_number: '',
  title: '',
  description: '',
  client_id: '',
  practice_area: '',
  status: 'open',
  priority: 'medium',
  jurisdiction: '',
  court: '',
  court_case_number: '',
  opposing_counsel_id: '',
  statute_of_limitations: '',
  fee_arrangement: 'hourly',
  flat_fee_amount: '',
  contingency_percent: '',
  budget_amount: '',
};

// First-matter quick-start templates. Shown ONLY when the firm has zero
// matters so far — meant to lower the activation barrier ("I don't know
// what to put here"). Each template fills the form with realistic
// defaults the user can edit before saving.
const FIRST_MATTER_TEMPLATES = [
  {
    id: 'property_dispute',
    label: 'Property dispute',
    emoji: '🏠',
    description: 'Boundary, title, or encroachment matter',
    fill: {
      title:           'Property line dispute',
      practice_area:   'Property',
      fee_arrangement: 'flat_fee',
      flat_fee_amount: '4500',
      priority:        'high',
      description:     'Investigating boundary encroachment. Commission survey, attempt mediation before litigation.',
    },
  },
  {
    id: 'contract_review',
    label: 'Contract review',
    emoji: '📄',
    description: 'Supplier, employment, or licensing agreement',
    fill: {
      title:           'Contract review',
      practice_area:   'Corporate',
      fee_arrangement: 'hourly',
      priority:        'medium',
      description:     'Review and redline. Flag liability caps, indemnities, term/termination, and dispute-resolution clauses.',
    },
  },
  {
    id: 'family_matter',
    label: 'Family law matter',
    emoji: '👨‍👩‍👧',
    description: 'Divorce, custody, or inheritance',
    fill: {
      title:           'Family law matter',
      practice_area:   'Family',
      fee_arrangement: 'flat_fee',
      flat_fee_amount: '6000',
      priority:        'high',
      description:     'Initial intake and strategy session scheduled. Gather supporting documents and identify potential mediator.',
    },
  },
];

// ─── Page ─────────────────────────────────────────────────────────────────────
export default function MattersPage() {
  // eslint-disable-next-line no-unused-vars
  const { firmId, firm, refreshSignal, refresh: refreshFirm, loading: _firmLoading } = useFirm();
  const { userProfile } = useAuth();
  const [, navigate]   = useRoute();
  const log = useActivityLogger();

  const [matters,      setMatters]      = useState([]);
  const [contacts,     setContacts]     = useState([]);
  const [firmUsers,    setFirmUsers]    = useState([]);
  const [employees,    setEmployees]    = useState([]);
  const [loading,      setLoading]      = useState(true);
  const [fetchError,   setFetchError]   = useState(null);
  const [modalLoading, setModalLoading] = useState(false); // lazy modal data
  const [q,            setQ]            = useState('');
  const [statusFilter, setStatusFilter] = useState('active'); // 'active' = all except archived

  // Quick filter chips that layer ON TOP of the status filter. Persisted to
  // localStorage so a user who always opens "Mine" sees it on next visit.
  const QUICK_FILTER_KEY = 'lf_matters_quick_filter_v1';
  const [quickFilter, setQuickFilter] = useState(() => {
    try { return localStorage.getItem(QUICK_FILTER_KEY) || ''; }
    catch { return ''; }
  });
  useEffect(() => {
    try { localStorage.setItem(QUICK_FILTER_KEY, quickFilter); }
    catch { /* ignore */ }
  }, [quickFilter]);
  const [showNew,      setShowNew]      = useState(false);
  const [form,         setForm]         = useState(blankMatter);

  // Multi-select state — composite keys for team, plain user IDs for lead
  const [leadIds,  setLeadIds]  = useState([]);   // user IDs (login users only)
  const [teamKeys, setTeamKeys] = useState([]);   // composite keys (users + employees)
  const [saving,       setSaving]       = useState(false);
  const [submitError,  setSubmitError]  = useState(null);
  const [bulkArchiving, setBulkArchiving] = useState(false);

  // ── User-driven bulk-select (separate from the retention auto-archive
  //    feature above). Keyed by matter id, scoped to the current filtered
  //    view — clears whenever the filter changes.
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [bulkBusy,    setBulkBusy]    = useState(false);
  const toggleSelected = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };
  const clearSelected = () => setSelectedIds(new Set());

  // Bulk actions — close, archive, reopen — run sequentially so a partial
  // failure doesn't leave the UI inconsistent. Optimistically remove the
  // affected matters from the list afterwards.
  const runBulk = async (label, fn) => {
    const ids = Array.from(selectedIds);
    if (ids.length === 0) return;
    if (!window.confirm(`${label} ${ids.length} matter${ids.length === 1 ? '' : 's'}? This can be undone individually.`)) return;
    setBulkBusy(true);
    let okCount = 0;
    let errCount = 0;
    for (const id of ids) {
      try {
        const { error } = await fn(id);
        if (error) errCount += 1; else okCount += 1;
      } catch { errCount += 1; }
    }
    setBulkBusy(false);
    if (okCount > 0) {
      // Re-fetch the matters list so statuses reflect the new state.
      const { data } = await listMatters(firmId);
      setMatters(data || []);
    }
    clearSelected();
    if (errCount > 0) {
      toast.error(`${okCount} updated, ${errCount} failed.`);
    } else if (okCount > 0) {
      toast.success(`${okCount} matter${okCount === 1 ? '' : 's'} updated.`);
    }
  };
  const handleBulkClose    = () => runBulk('Close',   (id) => closeMatter(id));
  const handleBulkArchiveSelected = () => runBulk('Archive', (id) => archiveMatter(id));
  const handleBulkReopen   = () => runBulk('Reopen',  (id) => reopenMatter(id));

  // Page load: only fetch matters — contacts/users/employees are loaded lazily
  // when the "New case file" modal opens so the list doesn't trigger 4 queries
  // on every tab-switch or refreshSignal bump.
  useEffect(() => {
    if (!firmId) { setLoading(false); return; }
    setLoading(true);
    setFetchError(null);
    withTimeout(listMatters(firmId), 15000)
      .then(({ data, error }) => {
        if (error) throw error;
        setMatters(data || []);
      })
      .catch((err) => setFetchError(err?.message || 'Failed to load case files. Please retry.'))
      .finally(() => setLoading(false));
  }, [firmId, refreshSignal]);

  // Lazy-load modal data the first time the new-matter modal is opened.
  // Subsequent opens reuse the already-fetched lists.
  const openNewModal = useCallback(async () => {
    setShowNew(true);
    if (contacts.length || firmUsers.length || employees.length) return; // already loaded
    setModalLoading(true);
    const [{ data: c }, { data: u }, { data: emp }] = await Promise.all([
      listContacts(firmId),
      listFirmUsers(firmId),
      listEmployees(firmId),
    ]);
    setContacts(c || []);
    setFirmUsers(u || []);
    setEmployees(emp || []);
    setModalLoading(false);
  }, [firmId, contacts.length, firmUsers.length, employees.length]);

  // ── Dropdown option lists ──
  const leadOptions = useMemo(() =>
    (firmUsers || []).map((u) => ({
      value:    u.id,
      label:    fullName(u) || u.email,
      sublabel: u.role,
    })),
  [firmUsers]);

  const teamOptions = useMemo(() => {
    const users = (firmUsers || []).map((u) => ({
      value:    userKey(u.id),
      label:    fullName(u) || u.email,
      sublabel: u.role,
    }));
    const emps = (employees || []).map((e) => ({
      value:    employeeKey(e.id),
      label:    fullName(e),
      sublabel: e.role || e.title || 'Employee',
      badge:    'Staff',
    }));
    return [...users, ...emps].sort((a, b) => a.label.localeCompare(b.label));
  }, [firmUsers, employees]);

  // ── Filter matters table ──
  // 'active' = everything except archived; '' = all; specific value = exact match
  const filtered = useMemo(() =>
    matters
      .filter((m) => {
        if (!statusFilter || statusFilter === 'active') return statusFilter === 'active' ? m.status !== 'archived' : true;
        return m.status === statusFilter;
      })
      // Quick filter chips — layered on top of status.
      .filter((m) => {
        if (!quickFilter) return true;
        if (quickFilter === 'mine')      return m.responsible_attorney_id === userProfile?.id;
        if (quickFilter === 'urgent')    return ['high','critical'].includes(m.priority) && ['open','in_progress','pending'].includes(m.status);
        if (quickFilter === 'recent')    {
          // Opened within last 30 days
          const d = m.open_date || m.created_at;
          if (!d) return false;
          return (Date.now() - new Date(d).getTime()) <= 30 * 86400000;
        }
        return true;
      })
      .filter((m) => {
        if (!q) return true;
        const s = q.toLowerCase();
        return (
          m.title?.toLowerCase().includes(s) ||
          m.matter_number?.toLowerCase().includes(s) ||
          m.practice_area?.toLowerCase().includes(s) ||
          fullName(m.client)?.toLowerCase().includes(s)
        );
      }),
  [matters, q, statusFilter, quickFilter, userProfile?.id]);

  // Status tab counts
  const counts = useMemo(() => ({
    active:   matters.filter((m) => m.status !== 'archived').length,
    open:     matters.filter((m) => m.status === 'open').length,
    closed:   matters.filter((m) => m.status === 'closed').length,
    archived: matters.filter((m) => m.status === 'archived').length,
  }), [matters]);

  // Retention flagging
  const retentionMonths = firm?.matter_retention_months ?? 12;
  const isReadyToArchive = (m) => {
    if (m.status !== 'closed' || !m.closed_at) return false;
    const closedMs = new Date(m.closed_at).getTime();
    const cutoffMs = Date.now() - retentionMonths * 30.44 * 24 * 60 * 60 * 1000;
    return closedMs < cutoffMs;
  };
  const readyToArchiveIds = useMemo(
    () => matters.filter(isReadyToArchive).map((m) => m.id),
    [matters, retentionMonths], // eslint-disable-line react-hooks/exhaustive-deps
  );

  const handleBulkArchive = async () => {
    if (!readyToArchiveIds.length) return;
    if (!window.confirm(`Archive ${readyToArchiveIds.length} matter${readyToArchiveIds.length !== 1 ? 's' : ''} that have exceeded the ${retentionMonths}-month retention period?`)) return;
    setBulkArchiving(true);
    await Promise.all(readyToArchiveIds.map((id) => archiveMatter(id)));
    setMatters((prev) => prev.map((m) =>
      readyToArchiveIds.includes(m.id) ? { ...m, status: 'archived', archived_at: new Date().toISOString() } : m
    ));
    setBulkArchiving(false);
  };

  const nextMatterNumber = () => {
    const year  = new Date().getFullYear();
    const count = matters.filter((m) => m.matter_number?.startsWith(String(year))).length + 1;
    return `${year}-${String(count).padStart(4, '0')}`;
  };

  const resetModal = () => { setForm(blankMatter); setLeadIds([]); setTeamKeys([]); setSubmitError(null); };

  const onSubmit = async (e) => {
    e.preventDefault();
    setSubmitError(null);
    if (!firmId) {
      refreshFirm(true);
      setSubmitError('Your firm data is still loading. Please wait a moment and try again.');
      return;
    }
    setSaving(true);

    // First lead attorney becomes the responsible_attorney_id on the matter row
    const primaryLeadId = leadIds[0] || userProfile?.id || null;

    const payload = {
      ...form,
      matter_number:           form.matter_number || nextMatterNumber(),
      responsible_attorney_id: primaryLeadId,
      statute_of_limitations:  form.statute_of_limitations  || null,
      flat_fee_amount:         form.flat_fee_amount          ? Number(form.flat_fee_amount)    : null,
      contingency_percent:     form.contingency_percent      ? Number(form.contingency_percent) : null,
      budget_amount:           form.budget_amount            ? Number(form.budget_amount)       : null,
      client_id:               form.client_id               || null,
      opposing_counsel_id:     form.opposing_counsel_id     || null,
    };

    const { data, error } = await createMatter(firmId, payload);
    if (error) { setSaving(false); setSubmitError(error.message); return; }

    const matterId = data.id;
    log('matter', data.id, 'created', `Case file "${data.title}" opened`);

    // Save lead attorneys → matter_users as 'lead_attorney'
    await Promise.all(leadIds.map((uid) => addMatterUser(firmId, matterId, uid, 'lead_attorney')));

    // Save team members — route to correct table
    const leadSet = new Set(leadIds);
    await Promise.all(
      teamKeys.map((key) => {
        if (key.startsWith('u:')) {
          const uid = key.slice(2);
          if (!leadSet.has(uid)) return addMatterUser(firmId, matterId, uid, 'team_member');
        } else if (key.startsWith('e:')) {
          return addMatterTeamMember(firmId, matterId, key.slice(2), 'team_member');
        }
        return null;
      }).filter(Boolean)
    );

    setSaving(false);
    setMatters((prev) => [data, ...prev]);
    setShowNew(false);
    resetModal();
  };

  const changeMatterStatus = async (matter, newStatus) => {
    const prev = matters;
    setMatters((ms) => ms.map((m) => m.id === matter.id ? { ...m, status: newStatus } : m));
    const { error } = await updateMatter(matter.id, { status: newStatus });
    if (error) { console.error('Status update error:', error); setMatters(prev); return; }
    log('matter', matter.id, 'status_changed', `Case "${matter.title}" → ${newStatus}`, matter.id);
  };

  const clientOptions = contacts
    .filter((c) => c.contact_type === 'client')
    .map((c) => ({ value: c.id, label: fullName(c) || c.organization || '—' }));
  const ocOptions = contacts
    .filter((c) => c.contact_type === 'opposing_counsel')
    .map((c) => ({ value: c.id, label: fullName(c) || c.organization || '—' }));

  return (
    <div className="space-y-6">
      <div className="flex items-end justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Case Files</h1>
          <p className="text-sm text-slate-500 dark:text-slate-400 mt-0.5">
            {counts.open} open · {counts.closed} closed · {counts.archived} archived
          </p>
        </div>
        <Button icon={Plus} onClick={openNewModal}>New case file</Button>
      </div>

      <FirstVisitTips
        pageKey="matters"
        title="Your case files live here"
        tips={[
          'Click "New case file" to open one — every matter has its own tasks, time entries, documents, and billing.',
          'Use the quick filters (🔥 Urgent, 👤 Mine) to focus on what needs your attention.',
          'Select rows with the checkboxes for bulk Close / Archive operations.',
          'Press ⌘K from anywhere to jump straight to a matter by name.',
        ]}
      />

      <Card>
        {/* Status tabs */}
        <div className="px-4 pt-4 pb-0 border-b border-slate-100 dark:border-slate-800">
          <div className="flex gap-1 overflow-x-auto">
            {[
              { value: 'active',   label: 'Active',   count: counts.active   },
              { value: 'open',     label: 'Open',     count: counts.open     },
              { value: 'closed',   label: 'Closed',   count: counts.closed   },
              { value: 'archived', label: 'Archived', count: counts.archived },
              { value: '',         label: 'All',      count: matters.length  },
            ].map((tab) => {
              const isActive = statusFilter === tab.value;
              return (
                <button
                  key={tab.value}
                  onClick={() => setStatusFilter(tab.value)}
                  className={classNames(
                    'inline-flex items-center gap-2 px-4 py-2 rounded-t-lg text-sm font-medium transition-all duration-150 whitespace-nowrap border-b-2 -mb-px',
                    isActive
                      ? 'bg-indigo-600/10 dark:bg-indigo-500/15 border-indigo-600 dark:border-indigo-400 text-indigo-700 dark:text-indigo-300'
                      : 'border-transparent text-slate-500 dark:text-slate-400 hover:text-slate-800 dark:hover:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-800/50',
                  )}
                >
                  {tab.label}
                  <span className={classNames(
                    'text-xs font-bold px-1.5 py-0.5 rounded-full min-w-[20px] text-center leading-4',
                    isActive
                      ? 'bg-indigo-600 text-white'
                      : 'bg-slate-200 dark:bg-slate-700 text-slate-500 dark:text-slate-400',
                  )}>
                    {tab.count}
                  </span>
                </button>
              );
            })}
          </div>
        </div>
        {/* Ready-to-archive banner — shown only on Closed tab when there are eligible matters */}
        {statusFilter === 'closed' && readyToArchiveIds.length > 0 && (
          <div className="flex items-center justify-between gap-3 px-4 py-3 bg-amber-50 dark:bg-amber-900/20 border-b border-amber-200 dark:border-amber-800">
            <div className="flex items-center gap-2 text-sm text-amber-800 dark:text-amber-300">
              <Archive size={15} className="flex-shrink-0" />
              <span>
                <span className="font-semibold">{readyToArchiveIds.length} matter{readyToArchiveIds.length !== 1 ? 's' : ''}</span> {readyToArchiveIds.length !== 1 ? 'have' : 'has'} exceeded the {retentionMonths}-month retention period and {readyToArchiveIds.length !== 1 ? 'are' : 'is'} ready to archive.
              </span>
            </div>
            <Button
              size="sm"
              variant="secondary"
              icon={Archive}
              loading={bulkArchiving}
              onClick={handleBulkArchive}
            >
              Archive all eligible
            </Button>
          </div>
        )}
        {/* Quick filter chips — daily-use shortcuts layered on top of status */}
        <div className="px-4 pt-3 pb-3 border-b border-slate-100 dark:border-slate-800 flex items-center gap-2 flex-wrap">
          <span className="text-[10px] uppercase tracking-wider font-semibold text-slate-400">Quick filter:</span>
          {[
            { value: '',       label: 'Any',     emoji: '✨' },
            { value: 'mine',   label: 'Mine',    emoji: '👤' },
            { value: 'urgent', label: 'Urgent',  emoji: '🔥' },
            { value: 'recent', label: 'Recent',  emoji: '🆕' },
          ].map((chip) => {
            const isActive = quickFilter === chip.value;
            return (
              <button
                key={chip.value}
                onClick={() => setQuickFilter(chip.value)}
                className={classNames(
                  'inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium transition',
                  isActive
                    ? 'bg-indigo-600 text-white'
                    : 'bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-700',
                )}
              >
                <span aria-hidden>{chip.emoji}</span> {chip.label}
              </button>
            );
          })}
        </div>

        {/* Bulk action bar — appears when one or more rows are selected */}
        {selectedIds.size > 0 && (
          <div className="px-4 py-2.5 bg-indigo-50 dark:bg-indigo-900/30 border-b border-indigo-200 dark:border-indigo-800 flex items-center justify-between gap-3 flex-wrap">
            <div className="flex items-center gap-2 text-sm text-indigo-900 dark:text-indigo-100">
              <span className="font-semibold">{selectedIds.size}</span>
              <span>matter{selectedIds.size === 1 ? '' : 's'} selected</span>
              <button
                onClick={clearSelected}
                className="ml-2 text-xs text-indigo-700 dark:text-indigo-300 underline hover:no-underline"
              >
                clear
              </button>
            </div>
            <div className="flex items-center gap-2">
              <Button size="sm" variant="ghost" disabled={bulkBusy} onClick={handleBulkReopen}>
                Reopen
              </Button>
              <Button size="sm" variant="ghost" disabled={bulkBusy} onClick={handleBulkClose}>
                Close
              </Button>
              <Button size="sm" variant="secondary" icon={Archive} disabled={bulkBusy} onClick={handleBulkArchiveSelected}>
                Archive
              </Button>
            </div>
          </div>
        )}

        {/* Search */}
        <div className="p-4 border-b border-slate-100 dark:border-slate-800">
          <div className="relative">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
            <Input
              placeholder="Search by title, matter #, client…"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              className="pl-9"
            />
          </div>
        </div>

        {loading ? (
          <SkeletonListRows rows={6} />
        ) : fetchError ? (
          <div className="p-8 flex flex-col items-center gap-3 text-center">
            <p className="text-sm text-red-600 dark:text-red-400">{fetchError}</p>
            <Button size="sm" onClick={() => { setFetchError(null); setLoading(true); withTimeout(listMatters(firmId), 15000).then(({ data }) => setMatters(data || [])).catch((e) => setFetchError(e?.message)).finally(() => setLoading(false)); }}>
              Retry
            </Button>
          </div>
        ) : !filtered.length ? (
          matters.length === 0 ? (
            // True empty — onboarding-focused message
            <EmptyState
              icon={Briefcase}
              title="Open your first case file"
              description="Case files are how DocketFlow organizes everything for a single matter — deadlines, time, documents, billing, and client communication all live together. Create one to get started, or load sample data from the dashboard checklist to explore first."
              action={<Button icon={Plus} onClick={openNewModal}>New case file</Button>}
            />
          ) : (
            // Filters hide everything — keep the simpler message
            <EmptyState
              icon={Briefcase}
              title="No case files match your filters"
              description="Try clearing the search or status filters above."
              action={<Button icon={Plus} onClick={openNewModal}>New case file</Button>}
            />
          )
        ) : (
          <>
            {/* Mobile card list */}
            <ul className="sm:hidden divide-y divide-slate-100 dark:divide-slate-800">
              {filtered.map((m) => {
                const priority = MATTER_PRIORITY.find((p) => p.value === m.priority);
                // Swipe actions vary by current matter status.
                const swipeActions = m.status === 'archived'
                  ? [{ label: 'Reopen', color: 'green', icon: FolderOpen, onAction: async () => { await reopenMatter(m.id); const { data } = await listMatters(firmId); setMatters(data || []); } }]
                  : m.status === 'closed'
                  ? [
                      { label: 'Archive', color: 'amber', icon: Archive,     onAction: async () => { await archiveMatter(m.id); const { data } = await listMatters(firmId); setMatters(data || []); } },
                      { label: 'Reopen',  color: 'green', icon: FolderOpen,  onAction: async () => { await reopenMatter(m.id);  const { data } = await listMatters(firmId); setMatters(data || []); } },
                    ]
                  : [
                      { label: 'Close',   color: 'slate', icon: CheckCircle2, onAction: async () => { await closeMatter(m.id);   const { data } = await listMatters(firmId); setMatters(data || []); } },
                      { label: 'Archive', color: 'amber', icon: Archive,      onAction: async () => { await archiveMatter(m.id); const { data } = await listMatters(firmId); setMatters(data || []); } },
                    ];
                return (
                  <li key={m.id}>
                    <SwipeableRow
                      rightActions={swipeActions}
                      onTap={() => navigate(`/matters/${m.id}`)}
                    >
                      <div className="flex items-start gap-3 px-4 py-3 active:bg-slate-50 dark:active:bg-slate-800">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <p className="font-medium text-sm truncate">{m.title}</p>
                            {m.confidential && (
                              <span
                                title="Confidential matter — restricted to leaders + assigned team"
                                className="inline-flex items-center gap-1 text-[10px] font-semibold px-1.5 py-0.5 rounded-full bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400 whitespace-nowrap"
                              >
                                🔒 Confidential
                              </span>
                            )}
                            {isReadyToArchive(m) && (
                              <span className="inline-flex items-center gap-1 text-[10px] font-semibold px-1.5 py-0.5 rounded-full bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400 whitespace-nowrap">
                                <Archive size={9} /> Ready to archive
                              </span>
                            )}
                          </div>
                          <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">
                            {m.matter_number}{fullName(m.client) ? ` · ${fullName(m.client)}` : ''}
                          </p>
                          {m.practice_area && (
                            <p className="text-xs text-slate-400 dark:text-slate-500 mt-0.5">{m.practice_area}</p>
                          )}
                        </div>
                        <div className="flex flex-col items-end gap-1 flex-shrink-0">
                          <div onClick={(e) => e.stopPropagation()}>
                            <StatusDropdown value={m.status} options={MATTER_STATUS} onChange={(s) => changeMatterStatus(m, s)} />
                          </div>
                          {priority && <Badge tone={priority.tone}>{priority.label}</Badge>}
                        </div>
                      </div>
                    </SwipeableRow>
                  </li>
                );
              })}
            </ul>

            {/* Desktop table */}
            <div className="hidden sm:block overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="text-xs uppercase tracking-wide text-slate-500 dark:text-slate-400 bg-slate-50 dark:bg-slate-800/40">
                  <tr>
                    <th className="w-10 text-center px-3 py-2">
                      <input
                        type="checkbox"
                        aria-label="Select all"
                        // Indeterminate when some-but-not-all visible rows are selected.
                        ref={(el) => {
                          if (!el) return;
                          const all  = filtered.length > 0 && filtered.every((m) => selectedIds.has(m.id));
                          const some = filtered.some((m) => selectedIds.has(m.id));
                          el.checked = all;
                          el.indeterminate = some && !all;
                        }}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setSelectedIds(new Set(filtered.map((m) => m.id)));
                          } else {
                            clearSelected();
                          }
                        }}
                        className="rounded border-slate-300 dark:border-slate-600 text-indigo-600 focus:ring-indigo-500 cursor-pointer"
                      />
                    </th>
                    <th className="text-left px-4 py-2 font-medium">Case File</th>
                    <th className="text-left px-4 py-2 font-medium">Client</th>
                    <th className="text-left px-4 py-2 font-medium">Practice</th>
                    <th className="text-left px-4 py-2 font-medium">Status</th>
                    <th className="text-left px-4 py-2 font-medium">Priority</th>
                    <th className="text-left px-4 py-2 font-medium">Opened</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                  {filtered.map((m) => {
                    const priority = MATTER_PRIORITY.find((p) => p.value === m.priority);
                    const isSelected = selectedIds.has(m.id);
                    return (
                      <tr
                        key={m.id}
                        className={classNames(
                          'cursor-pointer transition',
                          isSelected ? 'bg-indigo-50/60 dark:bg-indigo-900/15' : 'hover:bg-slate-50 dark:hover:bg-slate-800',
                        )}
                        onClick={() => navigate(`/matters/${m.id}`)}
                      >
                        <td className="w-10 text-center px-3 py-3" onClick={(e) => { e.stopPropagation(); toggleSelected(m.id); }}>
                          <input
                            type="checkbox"
                            checked={isSelected}
                            onChange={() => toggleSelected(m.id)}
                            onClick={(e) => e.stopPropagation()}
                            className="rounded border-slate-300 dark:border-slate-600 text-indigo-600 focus:ring-indigo-500 cursor-pointer"
                            aria-label={`Select ${m.title}`}
                          />
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-2 flex-wrap">
                            <p className="font-medium">{m.title}</p>
                            {m.confidential && (
                              <span
                                title="Confidential matter — restricted to leaders + assigned team"
                                className="inline-flex items-center gap-1 text-[10px] font-semibold px-1.5 py-0.5 rounded-full bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400 whitespace-nowrap"
                              >
                                🔒 Confidential
                              </span>
                            )}
                            {isReadyToArchive(m) && (
                              <span className="inline-flex items-center gap-1 text-[10px] font-semibold px-1.5 py-0.5 rounded-full bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400 whitespace-nowrap">
                                <Archive size={9} /> Ready to archive
                              </span>
                            )}
                          </div>
                          <p className="text-xs text-slate-500 dark:text-slate-400">{m.matter_number}</p>
                        </td>
                        <td className="px-4 py-3">{fullName(m.client) || '—'}</td>
                        <td className="px-4 py-3 text-slate-600 dark:text-slate-300">{m.practice_area || '—'}</td>
                        <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}>
                          <StatusDropdown value={m.status} options={MATTER_STATUS} onChange={(s) => changeMatterStatus(m, s)} />
                        </td>
                        <td className="px-4 py-3"><Badge tone={priority?.tone}>{priority?.label}</Badge></td>
                        <td className="px-4 py-3 text-slate-500 dark:text-slate-400">{formatDate(m.open_date || m.created_at)}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </>
        )}
      </Card>

      {/* ── New case file modal ── */}
      <Modal
        open={showNew}
        onClose={() => { setShowNew(false); resetModal(); }}
        title="New case file"
        size="lg"
        footer={
          <>
            <Button variant="ghost" onClick={() => { setShowNew(false); resetModal(); }}>Cancel</Button>
            <Button loading={saving} onClick={onSubmit} disabled={saving}>Create case file</Button>
          </>
        }
      >
        {submitError && (
          <div className="mb-4 rounded-lg bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 px-4 py-3 text-sm text-red-700 dark:text-red-300">
            {submitError}
          </div>
        )}
        {modalLoading ? (
          <div className="flex items-center justify-center py-12 text-slate-400 text-sm gap-2">
            <svg className="animate-spin w-4 h-4" viewBox="0 0 24 24" fill="none">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/>
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z"/>
            </svg>
            Loading…
          </div>
        ) : (
        <>
        {/* Quick-start templates — only on the very first matter */}
        {matters.length === 0 && !form.title && (
          <div className="mb-5 p-4 rounded-xl bg-gradient-to-br from-indigo-50 to-violet-50 dark:from-indigo-900/20 dark:to-violet-900/20 border border-indigo-100 dark:border-indigo-800/50">
            <p className="text-sm font-semibold text-slate-900 dark:text-white">
              Start from a template
            </p>
            <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">
              Pre-fills realistic defaults you can edit before saving.
            </p>
            <div className="mt-3 grid grid-cols-1 sm:grid-cols-3 gap-2">
              {FIRST_MATTER_TEMPLATES.map((t) => (
                <button
                  key={t.id}
                  type="button"
                  onClick={() => setForm((f) => ({ ...f, ...t.fill }))}
                  className="text-left p-3 rounded-lg bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 hover:border-indigo-400 hover:shadow-sm transition"
                >
                  <div className="flex items-center gap-2 mb-1">
                    <span className="text-lg">{t.emoji}</span>
                    <span className="text-sm font-medium text-slate-900 dark:text-white">{t.label}</span>
                  </div>
                  <p className="text-xs text-slate-500 dark:text-slate-400">{t.description}</p>
                </button>
              ))}
            </div>
          </div>
        )}
        <form onSubmit={onSubmit} className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="sm:col-span-2">
            <Label required>Title</Label>
            <Input
              required
              value={form.title}
              onChange={(e) => setForm({ ...form, title: e.target.value })}
              placeholder="Smith v. Jones — Contract Dispute"
            />
          </div>
          <div>
            <Label>Case file number</Label>
            <Input
              value={form.matter_number}
              onChange={(e) => setForm({ ...form, matter_number: e.target.value })}
              placeholder={`auto: ${nextMatterNumber()}`}
            />
          </div>
          <div>
            <Label>Practice area</Label>
            <Select
              placeholder="Select…"
              options={PRACTICE_AREAS}
              value={form.practice_area}
              onChange={(e) => setForm({ ...form, practice_area: e.target.value })}
            />
          </div>
          <div>
            <Label>Client</Label>
            <Select
              placeholder="Select…"
              options={clientOptions}
              value={form.client_id}
              onChange={(e) => setForm({ ...form, client_id: e.target.value })}
            />
          </div>
          <div>
            <Label>Opposing counsel</Label>
            <Select
              placeholder="None"
              options={ocOptions}
              value={form.opposing_counsel_id}
              onChange={(e) => setForm({ ...form, opposing_counsel_id: e.target.value })}
            />
          </div>

          {/* ── Assignment ── */}
          <div>
            <Label>Lead attorney</Label>
            <MultiSelectDropdown
              placeholder="Select attorneys…"
              options={leadOptions}
              selected={leadIds}
              onChange={(id) => setLeadIds((prev) =>
                prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
              )}
            />
          </div>
          <div>
            <Label>Team members</Label>
            <MultiSelectDropdown
              placeholder="Select team members…"
              options={teamOptions}
              selected={teamKeys}
              onChange={(key) => setTeamKeys((prev) =>
                prev.includes(key) ? prev.filter((x) => x !== key) : [...prev, key]
              )}
            />
          </div>

          <div>
            <Label>Jurisdiction</Label>
            {/* Dropdown of canonical Ghana court forums (1992 Constitution
                Art. 126 + Courts Act 1993). Free-text input was creating
                inconsistent values across matters — "Circuit Court",
                "circuit court", "circuit ct" all looked different to
                search and reports. The structured list also sets up
                future features that depend on jurisdiction (filing-fee
                lookup, procedural rule reminders, deadline templates). */}
            <Select
              placeholder="Select court / forum…"
              options={GHANA_JURISDICTIONS}
              value={form.jurisdiction}
              onChange={(e) => setForm({ ...form, jurisdiction: e.target.value })}
            />
          </div>
          <div>
            <Label>Court</Label>
            <Input
              value={form.court}
              onChange={(e) => setForm({ ...form, court: e.target.value })}
              placeholder="Court name"
            />
          </div>
          <div>
            <Label>Court case number</Label>
            <Input
              value={form.court_case_number}
              onChange={(e) => setForm({ ...form, court_case_number: e.target.value })}
            />
          </div>
          <div>
            <Label>
              Statute of limitations
              <HelpTooltip>
                The date after which a claim can no longer be brought. DocketFlow shows a red countdown on the dashboard as this date approaches — leave blank if not applicable.
              </HelpTooltip>
            </Label>
            <Input
              type="date"
              value={form.statute_of_limitations}
              onChange={(e) => setForm({ ...form, statute_of_limitations: e.target.value })}
            />
          </div>
          <div>
            <Label>Status</Label>
            <Select
              options={MATTER_STATUS.map((s) => ({ value: s.value, label: s.label }))}
              value={form.status}
              onChange={(e) => setForm({ ...form, status: e.target.value })}
            />
          </div>
          <div>
            <Label>Priority</Label>
            <Select
              options={MATTER_PRIORITY.map((p) => ({ value: p.value, label: p.label }))}
              value={form.priority}
              onChange={(e) => setForm({ ...form, priority: e.target.value })}
            />
          </div>
          <div>
            <Label>
              Fee arrangement
              <HelpTooltip>
                How you'll bill: <strong>Hourly</strong> tracks time entries; <strong>Flat fee</strong> sets a fixed amount up front; <strong>Contingency</strong> takes a percentage of recovery; <strong>Retainer</strong> bills against a trust deposit.
              </HelpTooltip>
            </Label>
            <Select
              options={FEE_ARRANGEMENTS}
              value={form.fee_arrangement}
              onChange={(e) => setForm({ ...form, fee_arrangement: e.target.value })}
            />
          </div>
          {form.fee_arrangement === 'flat_fee' && (
            <div>
              <Label>Flat fee amount</Label>
              <Input
                type="number" step="0.01"
                value={form.flat_fee_amount}
                onChange={(e) => setForm({ ...form, flat_fee_amount: e.target.value })}
              />
            </div>
          )}
          {form.fee_arrangement === 'contingency' && (
            <div>
              <Label>Contingency %</Label>
              <Input
                type="number" step="0.1"
                value={form.contingency_percent}
                onChange={(e) => setForm({ ...form, contingency_percent: e.target.value })}
              />
            </div>
          )}
          <div>
            <Label>Matter budget</Label>
            <Input
              type="number" step="0.01"
              value={form.budget_amount}
              onChange={(e) => setForm({ ...form, budget_amount: e.target.value })}
            />
          </div>
          <div className="sm:col-span-2">
            <Label>Description</Label>
            <Textarea
              rows={3}
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
            />
          </div>
        </form>
        </>
        )}
      </Modal>
    </div>
  );
}
